import type { AxiosInstance, AxiosResponse } from 'axios'

import type { LocationGroupsRepository } from '../application/LocationGroupsRepository'
import { type LocationGroup, type LocationGroupDraft, normalizeLocationGroupDraft } from '../domain/locationGroup'
import { errorFromResponse, mapLocationGroupError } from './locationGroupErrors'
import { createLocationGroupsHttpClient } from './locationGroupsHttpClient'

type IdDTO = { id: string }
const path = (storeId: string) => `/api/stores/${encodeURIComponent(storeId)}/location-groups`

function mapGroup(group: LocationGroup): LocationGroup {
  return {
    ...group,
    geolocations: (group.geolocations ?? []).map(location => ({
      ...location,
      availabilityType: location.availabilityType ?? 'AVAILABLE'
    }))
  }
}

function expectStatus<T>(response: AxiosResponse<T>, expected: number): T {
  if (response.status !== expected) throw errorFromResponse(response.status, response.data)
  return response.data
}

async function call<T>(operation: () => Promise<AxiosResponse<T>>, expected: number): Promise<T> {
  try {
    return expectStatus(await operation(), expected)
  } catch (error) {
    throw mapLocationGroupError(error)
  }
}

export function createHttpLocationGroupsRepository(
  client: AxiosInstance = createLocationGroupsHttpClient()
): LocationGroupsRepository {
  return {
    async list(storeId, signal) {
      return (await call(() => client.get<LocationGroup[]>(path(storeId), { signal }), 200)).map(mapGroup)
    },
    async get(storeId, groupId, signal) {
      return mapGroup(
        await call(() => client.get<LocationGroup>(`${path(storeId)}/${encodeURIComponent(groupId)}`, { signal }), 200)
      )
    },
    async create(storeId, draft) {
      const result = await call(() => client.post<IdDTO>(path(storeId), normalizeLocationGroupDraft(draft)), 201)
      if (!result?.id) throw errorFromResponse(201, result)
      return result.id
    },
    async update(storeId, groupId, draft) {
      await call(
        () => client.put<void>(`${path(storeId)}/${encodeURIComponent(groupId)}`, normalizeLocationGroupDraft(draft)),
        204
      )
    },
    async remove(storeId, groupId) {
      await call(() => client.delete<void>(`${path(storeId)}/${encodeURIComponent(groupId)}`), 204)
    }
  }
}

export type { LocationGroupDraft }
